"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MoreHorizontal } from "lucide-react";
import { ButtonIcon, ButtonOutline } from "@/components/common/StandardButton";
import { Breadcrumb } from "@/components/common/Breadcrumb";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { orderActions } from "@/store/actions/order.actions";
import { updateOrderSetStage } from "@/services/orderService";
import CustomerInformation from "@/components/orders/CustomerInformation";
import OrderSetInvoice from "@/components/orders/OrderSetInvoice";
import OrderSetShippingDetails from "@/components/orders/OrderSetShippingDetails";
import OrderSetTrackingCard from "@/components/orders/OrderSetTrackingCard";
import SupportCard from "@/components/orders/OrderSetSupportCard";
import { OrderSetActionsBar } from "@/components/orders/OrderSetActionsBar";

export default function OrderSetDetailsPage() {
  const { orderId, orderSetId, storeType, storeId } = useParams<{
    orderId: string;
    orderSetId: string;
    storeType: string;
    storeId: string;
  }>();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { selectedOrder, detailsLoading } = useAppSelector(
    (state) => state.order,
  );
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  const statusLabels: Record<string, string> = {
    PENDING: "Pending",
    PROCESSING: "Processing",
    UNDER_CONFIRMATION: "Under Confirmation",
    CONFIRMED: "Confirmed",
    INVOICED: "Invoiced",
    DISPATCHING: "Dispatching",
    READY_TO_SHIP: "Ready to Ship",
    PICKUP_SCHEDULED: "Pickup Scheduled",
    PICKUP_QUEUED: "Pickup Queued",
    PICKUP_RESCHEDULED: "Pickup Rescheduled",
    PICKUP_EXCEPTION: "Pickup Exception",
    PICKUP_ERROR: "Pickup Error",
    DISPATCHED: "Dispatched",
    PICKED_UP: "Picked Up",
    SHIPPED: "Shipped",
    ARRIVED: "Arrived at Hub",
    IN_TRANSIT: "In Transit",
    OUT_FOR_DELIVERY: "Out for Delivery",
    DELIVERY_RETRY: "Delivery Retry",
    DELIVERY_DELAYED: "Delivery Delayed",
    RTO_INITIATED: "Return Initiated",
    RTO_IN_TRANSIT: "Return In Transit",
    RTO_DELIVERED: "Return Delivered",
    RTO_FAILED: "Return Failed",
    DELIVERED: "Delivered",
    CANCELLED: "Cancelled",
    REJECTED: "Rejected",
    NOT_REQUIRED: "Not Required",
    none: "None",
    "": "Not Set",
  };

  const statusFlow: Record<string, string[]> = {
    PENDING: ["PROCESSING"],
    PROCESSING: ["READY_TO_SHIP"],
    UNDER_CONFIRMATION: ["PROCESSING", "READY_TO_SHIP"],
    READY_TO_SHIP: ["PICKED_UP", "DISPATCHED", "SHIPPED"],
    DISPATCHING: ["READY_TO_SHIP", "DISPATCHED"],
    PICKUP_SCHEDULED: ["PICKUP_QUEUED", "PICKED_UP"],
    PICKUP_QUEUED: ["PICKED_UP"],
    PICKUP_RESCHEDULED: ["PICKUP_QUEUED", "PICKED_UP"],
    PICKUP_EXCEPTION: ["PICKUP_QUEUED"],
    PICKUP_ERROR: ["PICKUP_QUEUED"],
    DISPATCHED: ["IN_TRANSIT", "OUT_FOR_DELIVERY"],
    SHIPPED: ["IN_TRANSIT", "OUT_FOR_DELIVERY"],
    PICKED_UP: ["IN_TRANSIT"],
    ARRIVED: ["OUT_FOR_DELIVERY"],
    IN_TRANSIT: ["OUT_FOR_DELIVERY", "DELIVERY_DELAYED", "RTO_INITIATED"],
    DELIVERY_DELAYED: ["IN_TRANSIT", "OUT_FOR_DELIVERY"],
    OUT_FOR_DELIVERY: ["DELIVERED", "DELIVERY_RETRY"],
    DELIVERY_RETRY: ["OUT_FOR_DELIVERY"],
    RTO_INITIATED: ["RTO_IN_TRANSIT"],
    RTO_IN_TRANSIT: ["RTO_DELIVERED", "RTO_FAILED"],
    RTO_DELIVERED: [],
    RTO_FAILED: [],
    DELIVERED: [],
    CANCELLED: [],
    REJECTED: [],
    NOT_REQUIRED: [],
    none: [],
    "": [],
  };

  // Only fetch if we don't already have this order's details
  useEffect(() => {
    if (orderId && selectedOrder?.id !== orderId) {
      dispatch(orderActions.fetchOrderDetails(orderId));
    }
  }, [orderId, selectedOrder?.id, dispatch]);

  // Find the specific order set
  const orderSet = useMemo(() => {
    return selectedOrder?.order_sets?.find((os) => os.id === orderSetId);
  }, [selectedOrder, orderSetId]);

  const handleBackClick = () => {
    navigate(`/${storeType}/${storeId}/orders/${orderId}`);
  };

  const handleStatusUpdate = async (status: string) => {
    try {
      setUpdatingStatus(status);
      await updateOrderSetStage(orderId!, orderSetId!, status);
      dispatch(orderActions.fetchOrderDetails(orderId!));
    } catch (error) {
      console.error("Failed to update order set status:", error);
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Get available next statuses based on current status
  const getAvailableNextStatuses = () => {
    const currentStatus =
      orderSet?.current_shipping_stage || orderSet?.shipping_status || "";
    return statusFlow[currentStatus] || [];
  };

  const currentShippingStatus =
    orderSet?.current_shipping_stage || orderSet?.shipping_status;

  const breadcrumbItems = [
    {
      label: "Orders",
      href: `/${storeType}/${storeId}/orders`,
    },
    {
      label: `Order #${orderId}`,
      href: `/${storeType}/${storeId}/orders/${orderId}`,
    },
    {
      label: `Order Set`,
      isActive: true,
    },
  ];

  const getShippingStatusBadge = (status?: string) => {
    if (!status) return null;

    const displayStatus =
      statusLabels[status] ||
      status
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase());

    let badgeClass = "bg-gray-100 text-gray-800";

    switch (status) {
      case "PENDING":
      case "PROCESSING":
      case "UNDER_CONFIRMATION":
      case "CONFIRMED":
      case "INVOICED":
      case "DISPATCHING":
      case "READY_TO_SHIP":
        badgeClass = "bg-blue-100 text-blue-800";
        break;
      case "DISPATCHED":
      case "PICKED_UP":
      case "IN_TRANSIT":
      case "SHIPPED":
      case "ARRIVED":
      case "OUT_FOR_DELIVERY":
      case "DELIVERY_RETRY":
      case "DELIVERY_DELAYED":
        badgeClass = "bg-yellow-100 text-yellow-800";
        break;
      case "DELIVERED":
        badgeClass = "bg-green-100 text-green-800";
        break;
      case "RTO_INITIATED":
      case "RTO_IN_TRANSIT":
        badgeClass = "bg-orange-100 text-orange-800";
        break;
      case "RTO_DELIVERED":
        badgeClass = "bg-green-100 text-green-800";
        break;
      case "RTO_FAILED":
        badgeClass = "bg-red-100 text-red-800";
        break;
      case "CANCELLED":
        badgeClass = "bg-red-100 text-red-800";
        break;
    }

    return (
      <span
        className={`px-3 py-1 ${badgeClass} text-sm font-medium rounded-full`}
      >
        {displayStatus}
      </span>
    );
  };

  return (
    <div className="view min-h-screen bg-background gap-2.5 flex flex-col">
      {/* Breadcrumb */}
      <Breadcrumb items={breadcrumbItems} showBackButton={false} />

      {/* Header */}
      <div className="border-b border-border bg-card rounded-lg">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <ButtonOutline
              onClick={handleBackClick}
              className="flex items-center gap-2 px-3"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Order
            </ButtonOutline>

            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-lg font-semibold text-foreground">
                  Order Set Details
                </h1>
                <p className="text-sm text-muted-foreground">
                  Order #{orderId}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <ButtonOutline size="sm" className="text-xs">
              Print
            </ButtonOutline>
            <ButtonIcon variant="outline">
              <MoreHorizontal className="h-4 w-4" />
            </ButtonIcon>
          </div>
        </div>
      </div>

      {/* Main Content - Three Columns */}
      <div className="flex gap-2.5 flex-1">
        {/* Column 1 - Customer Info, Order Set Tracking */}
        <div className="column w-80 flex flex-col gap-2.5">
          <CustomerInformation orderId={orderId!} />
          <OrderSetTrackingCard
            orderSetId={orderSetId!}
            orderSet={orderSet}
          />
        </div>

        {/* Column 2 - Status, Shipping Details, Invoice, Support */}
        <div className="column flex-1 flex flex-col gap-2.5">
          {/* Status */}
          <div className="flex items-center justify-between p-4 bg-card rounded-lg border border-border">
            <div className="flex items-center gap-3">
              {detailsLoading ? (
                <div className="h-6 w-16 bg-muted rounded-full animate-pulse"></div>
              ) : (
                <>
                  {getShippingStatusBadge(currentShippingStatus)}
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-2 justify-end items-center">
              {getAvailableNextStatuses().map((status) => {
                const label =
                  statusLabels[status] ||
                  status
                    .replace(/_/g, " ")
                    .toLowerCase()
                    .replace(/\b\w/g, (l) => l.toUpperCase());

                return (
                  <button
                    key={status}
                    onClick={() => handleStatusUpdate(status)}
                    disabled={updatingStatus !== null}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
                  >
                    {updatingStatus === status
                      ? "Updating..."
                      : `Update to ${label}`}
                  </button>
                );
              })}

              {orderSet && selectedOrder && (
                <OrderSetActionsBar
                  orderSet={orderSet}
                  order={selectedOrder}
                  onUpdate={() => {
                    if (orderId) {
                      dispatch(orderActions.fetchOrderDetails(orderId));
                    }
                  }}
                />
              )}
            </div>
          </div>

          <div className="flex gap-2.5">
            <div className="column w-80 flex flex-col gap-2.5">
              <OrderSetShippingDetails
                orderSet={orderSet}
                loading={detailsLoading}
              />
              <SupportCard orderId={orderId!} orderSetId={orderSetId!} />
            </div>
            <OrderSetInvoice
              orderSet={orderSet}
              loading={detailsLoading}
              variants={selectedOrder?.variants}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
